import moment from "moment";
import { extractAssignmentsFromPayload, isCompletedAssignmentStatus } from "../../../utils/interpreterAssignment";

const pickFirstDefined = (...values) => {
  const found = values.find((value) => value != null && value !== "");

  return found ?? 0;
};

export const getInterpreterList = (payload) => {
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const getList = getInterpreterList;

const getStatsPayload = (stats) => {
  if (!stats || typeof stats !== "object" || Array.isArray(stats)) return {};

  const nested = stats.stats ?? stats.data?.stats;
  const data =
    stats.data && typeof stats.data === "object" && !Array.isArray(stats.data)
      ? stats.data
      : stats;

  return { ...data, ...(nested && typeof nested === "object" ? nested : {}) };
};

const isActiveInterpreter = (status) =>
  String(status || "").toLowerCase() === "active";

const isCompletedAssignment = (status) => isCompletedAssignmentStatus(status);

export const buildQueryParams = (searchText, filters) => {
  const params = { search: searchText };

  if (filters.status) params.status = filters.status;
  if (filters.sort) params.sort = filters.sort;

  return params;
};

export const getInterpreterAssignmentCounts = (interpreter) => {
  const assignments = Array.isArray(interpreter?.assignments)
    ? interpreter.assignments
    : [];
  const completedFromList = assignments.filter((assignment) =>
    isCompletedAssignment(assignment?.status),
  ).length;
  const totalFromList = assignments.length;

  const completed = Number(
    interpreter?.completed_assignments ?? completedFromList,
  );
  const total = Number(interpreter?.total_assignments ?? totalFromList);
  const incomplete = Number(
    interpreter?.incomplete_assignments ?? Math.max(0, total - completed),
  );

  return { completed, total, incomplete };
};

export const calculateInterpreterStats = (interpreters, stats) => {
  const interpreterData = getList(interpreters);
  const statsPayload = getStatsPayload(stats);
  const fromList = interpreterData.reduce(
    (acc, interpreter) => {
      const counts = getInterpreterAssignmentCounts(interpreter);
      acc.completed += counts.completed;
      acc.incomplete += counts.incomplete;
      if (isActiveInterpreter(interpreter.status)) acc.active += 1;
      return acc;
    },
    { active: 0, completed: 0, incomplete: 0 },
  );

  const listHasAssignmentData = interpreterData.some(
    (interpreter) =>
      interpreter?.assignments?.length ||
      interpreter?.completed_assignments != null ||
      interpreter?.total_assignments != null,
  );

  return {
    total: pickFirstDefined(
      statsPayload.total_interpreters,
      interpreterData.length,
    ),
    active: pickFirstDefined(statsPayload.active_interpreters, fromList.active),
    completed: listHasAssignmentData
      ? fromList.completed
      : pickFirstDefined(
          statsPayload.completed_assignments,
          statsPayload.complete,
          statsPayload.completed,
          fromList.completed,
        ),
    incomplete: listHasAssignmentData
      ? fromList.incomplete
      : pickFirstDefined(
          statsPayload.incomplete_assignments,
          statsPayload.incomplete,
          fromList.incomplete,
        ),
    averageTime: statsPayload.average_time || "0",
  };
};

export const normalizeAssignmentsList = (payload) =>
  extractAssignmentsFromPayload(payload);

export const mergeAssignmentLists = (...payloads) => {
  const byId = new Map();

  payloads.forEach((payload) => {
    normalizeAssignmentsList(payload).forEach((item) => {
      if (!item) return;
      const key = item._id || JSON.stringify(item);
      const existing = byId.get(key);

      if (!existing) {
        byId.set(key, item);
        return;
      }

      const existingComplete = isCompletedAssignmentStatus(existing.status);
      const incomingComplete = isCompletedAssignmentStatus(item.status);

      if (incomingComplete && !existingComplete) {
        byId.set(key, { ...existing, ...item, status: item.status });
        return;
      }

      if (existingComplete && !incomingComplete) {
        byId.set(key, { ...item, ...existing, status: existing.status });
        return;
      }

      byId.set(key, { ...existing, ...item });
    });
  });

  return Array.from(byId.values());
};

const normalizeAssignmentStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");

export const computeAssignmentStatsFromList = (assignments) => {
  const list = normalizeAssignmentsList(assignments);

  return list.reduce(
    (counts, assignment) => {
      const status = normalizeAssignmentStatus(assignment?.status);

      if (
        status === "complete" ||
        status === "completed" ||
        status === "finished" ||
        status === "submitted"
      ) {
        counts.completed += 1;
      } else if (status === "overdue" || status === "over_due") {
        counts.overdue += 1;
      } else if (
        status === "not_active" ||
        status === "not_assigned" ||
        status === "unassigned"
      ) {
        counts.unassigned += 1;
      } else if (status === "assigned") {
        counts.assigned += 1;
      } else {
        counts.incomplete += 1;
      }

      counts.total += 1;
      return counts;
    },
    {
      total: 0,
      completed: 0,
      assigned: 0,
      unassigned: 0,
      overdue: 0,
      incomplete: 0,
    },
  );
};

export const getInterpreterAssignmentDashboardStats = (assignmentsPayload) => {
  const list = normalizeAssignmentsList(
    assignmentsPayload?.data ?? assignmentsPayload,
  );
  const apiStats = getStatsPayload(assignmentsPayload?.stats);
  const fromList = computeAssignmentStatsFromList(list);
  const incompleteFromList = Math.max(0, fromList.total - fromList.completed);
  const useList = fromList.total > 0;

  return {
    totalAssignments: useList
      ? fromList.total
      : pickFirstDefined(
          apiStats.total,
          apiStats.total_videos,
          apiStats.total_assignments,
          fromList.total,
        ),
    completedAssignments: useList
      ? fromList.completed
      : pickFirstDefined(
          apiStats.complete,
          apiStats.completed,
          apiStats.completed_assignments,
          fromList.completed,
        ),
    incompleteAssignments: useList
      ? incompleteFromList
      : pickFirstDefined(
          apiStats.incomplete,
          apiStats.incomplete_assignments,
          fromList.incomplete + fromList.assigned + fromList.overdue,
          incompleteFromList,
        ),
    assignedAssignments: useList
      ? fromList.assigned
      : pickFirstDefined(
          apiStats.assigned,
          apiStats.assigned_assignments,
          fromList.assigned,
        ),
    overdueAssignments: useList
      ? fromList.overdue
      : pickFirstDefined(
          apiStats.overdue,
          apiStats.overdue_assignments,
          fromList.overdue,
        ),
    videosUnassigned: useList
      ? fromList.unassigned
      : pickFirstDefined(
          apiStats.unAssigned,
          apiStats.unassigned,
          apiStats.not_assigned,
          fromList.unassigned,
        ),
  };
};

export const getAssignmentOverviewStats = (statsData, listStats) => {
  const payload = getStatsPayload(statsData);
  const fallback = listStats ?? {};

  return [
    {
      value: pickFirstDefined(
        payload.total,
        payload.total_videos,
        payload.total_assignments,
        fallback.total,
      ),
      label: "Total Videos",
    },
    {
      value: pickFirstDefined(
        payload.assigned,
        payload.assigned_assignments,
        fallback.assigned,
      ),
      label: "Assigned",
    },
    {
      value: pickFirstDefined(
        payload.unAssigned,
        payload.unassigned,
        payload.not_assigned,
        payload.not_active,
        payload.videos_unassigned,
        fallback.unAssigned,
        fallback.unassigned,
      ),
      label: "Unassigned",
    },
    {
      value: pickFirstDefined(
        payload.complete,
        payload.completed,
        payload.completed_assignments,
        fallback.complete,
        fallback.completed,
      ),
      label: "Completed",
    },
  ];
};

export const buildEditInterpreterData = (record) => ({
  _id: record._id,
  status: record.status,
  interpreter: record.interpreter,
  experience: record.experience,
});

export const getInterpreterImageUrl = (interpreter) =>
  interpreter?.image || interpreter?.avatar || interpreter?.user?.image || null;

export const mapInterpretersToTableData = (interpreters) => {
  const interpretersData = getList(interpreters);

  return interpretersData.map((interpreter) => {
    const assignmentCounts = getInterpreterAssignmentCounts(interpreter);

    return {
      _id: interpreter._id,
      interpreter: {
        _id: interpreter._id,
        name: interpreter.user?.full_name,
        email: interpreter.user?.email,
        avatar: getInterpreterImageUrl(interpreter),
      },
      experience: interpreter.experience,
      assignments: interpreter.assignments || [],
      completedAssignments: assignmentCounts.completed,
      totalAssignments: assignmentCounts.total,
      status: interpreter.status,
      joinedDate: moment(interpreter.date_created).format("YYYY-MM-DD"),
      totalTime: "00:00:00",
      avgTime: "00:00",
    };
  });
};
